/**
 * IntakeInfoTab.jsx — Intake & Admission Tab for IndividualProfile
 *
 * Shows: admission status, intake demographics, insurance, clinical history,
 * program assignment, and discharge info if discharged.
 *
 * Powered by Cold Harbor Digital
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  INSURANCE_TYPES, PROGRAMS, REFERRAL_SOURCES,
} from "../services/intakeService";
import {
  CheckCircle, Clock, LogOut, User, Shield, DollarSign,
  Heart, Users, AlertTriangle, FileText, ChevronRight,
} from "lucide-react";

const S = {
  card: {
    background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 16, padding: 16, marginBottom: 12,
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 800, color: "#f6ad55",
    textTransform: "uppercase", letterSpacing: 1, marginBottom: 8,
  },
  row: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "6px 0", borderBottom: "1px solid rgba(99,179,237,0.04)",
  },
  rowLabel: { fontSize: 10, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 0.5 },
  rowValue: { fontSize: 13, color: "#e2e8f0", textAlign: "right" },
};

export default function IntakeInfoTab({ individual }) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const ind = individual;
  if (!ind) return null;

  const isAdmitted = ind.admissionStatus === "admitted";
  const isDischarged = ind.admissionStatus === "discharged";
  const statusColor = isDischarged ? "#718096" : isAdmitted ? "#68d391" : "#ecc94b";
  const statusLabel = isDischarged ? "Discharged" : isAdmitted ? "Active" : (ind.admissionStatus || "Unknown");
  const canDischarge = ["supervisor", "admin", "clinician"].includes(role);

  const formatDate = (d) => {
    if (!d) return "—";
    if (d.toDate) return d.toDate().toLocaleDateString();
    return new Date(d).toLocaleDateString();
  };

  return (
    <div>
      {/* Admission Status */}
      <div style={{
        ...S.card,
        borderColor: `${statusColor}22`,
        borderLeft: `3px solid ${statusColor}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isDischarged ? <LogOut size={16} style={{ color: statusColor }} /> :
              isAdmitted ? <CheckCircle size={16} style={{ color: statusColor }} /> :
              <Clock size={16} style={{ color: statusColor }} />
            }
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: statusColor, textTransform: "capitalize" }}>
                {statusLabel}
              </div>
              <div style={{ fontSize: 11, color: "#718096" }}>
                {ind.admissionDate && `Admitted ${formatDate(ind.admissionDate)}`}
                {isDischarged && ind.dischargeDate && ` · Discharged ${formatDate(ind.dischargeDate)}`}
              </div>
            </div>
          </div>
          {isAdmitted && canDischarge && (
            <button onClick={() => navigate(`/discharge/${ind.id}`)} style={{
              padding: "6px 14px", borderRadius: 10, fontSize: 11, fontWeight: 700,
              background: "rgba(252,129,129,0.06)", color: "#fc8181",
              border: "1px solid rgba(252,129,129,0.15)", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              <LogOut size={12} /> Discharge
            </button>
          )}
        </div>
      </div>

      {/* Discharge Info (if discharged) */}
      {isDischarged && (
        <div style={{ ...S.card, borderColor: "rgba(113,128,150,0.15)" }}>
          <div style={S.sectionLabel}>Discharge Information</div>
          {ind.dischargeReason && (
            <div style={S.row}>
              <span style={S.rowLabel}>Reason</span>
              <span style={S.rowValue}>{ind.dischargeReason}</span>
            </div>
          )}
          {ind.dischargeSummary && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", marginBottom: 4 }}>Summary</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{ind.dischargeSummary}</div>
            </div>
          )}
          {ind.aftercarePlan && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#4fd1c5", textTransform: "uppercase", marginBottom: 4 }}>Aftercare Plan</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{ind.aftercarePlan}</div>
            </div>
          )}
          {ind.dischargedByName && (
            <div style={{ fontSize: 10, color: "#718096", marginTop: 8 }}>
              Discharged by {ind.dischargedByName}
            </div>
          )}
        </div>
      )}

      {/* Insurance */}
      <div style={S.card}>
        <div style={S.sectionLabel}>
          <DollarSign size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
          Insurance
        </div>
        <div style={S.row}>
          <span style={S.rowLabel}>Type</span>
          <span style={S.rowValue}>
            {INSURANCE_TYPES.find(t => t.value === ind.insuranceType)?.label || ind.insuranceType || ind.insuranceProvider || "—"}
          </span>
        </div>
        {ind.insurancePrimary?.payerName && (
          <>
            <div style={S.row}>
              <span style={S.rowLabel}>Payer</span>
              <span style={S.rowValue}>{ind.insurancePrimary.payerName}</span>
            </div>
            {ind.insurancePrimary.memberId && (
              <div style={S.row}>
                <span style={S.rowLabel}>Member ID</span>
                <span style={{ ...S.rowValue, fontFamily: "monospace", fontSize: 12 }}>{ind.insurancePrimary.memberId}</span>
              </div>
            )}
            {ind.insurancePrimary.groupNumber && (
              <div style={S.row}>
                <span style={S.rowLabel}>Group #</span>
                <span style={{ ...S.rowValue, fontFamily: "monospace", fontSize: 12 }}>{ind.insurancePrimary.groupNumber}</span>
              </div>
            )}
            {ind.insurancePrimary.effectiveDate && (
              <div style={S.row}>
                <span style={S.rowLabel}>Effective</span>
                <span style={S.rowValue}>{ind.insurancePrimary.effectiveDate}</span>
              </div>
            )}
          </>
        )}
        {/* Legacy field fallback */}
        {!ind.insurancePrimary?.payerName && ind.medicaidId && (
          <div style={S.row}>
            <span style={S.rowLabel}>Medicaid ID</span>
            <span style={{ ...S.rowValue, fontFamily: "monospace", fontSize: 12 }}>{ind.medicaidId}</span>
          </div>
        )}
        {ind.insuranceSecondary?.payerName && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", marginTop: 8, marginBottom: 4 }}>Secondary Insurance</div>
            <div style={S.row}>
              <span style={S.rowLabel}>Payer</span>
              <span style={S.rowValue}>{ind.insuranceSecondary.payerName}</span>
            </div>
            {ind.insuranceSecondary.memberId && (
              <div style={S.row}>
                <span style={S.rowLabel}>Member ID</span>
                <span style={S.rowValue}>{ind.insuranceSecondary.memberId}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Program & Assignment */}
      <div style={S.card}>
        <div style={S.sectionLabel}>
          <Users size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
          Program & Assignment
        </div>
        <div style={S.row}>
          <span style={S.rowLabel}>Program</span>
          <span style={S.rowValue}>{PROGRAMS.find(p => p.value === ind.program)?.label || ind.program || "—"}</span>
        </div>
        {ind.referralSource && (
          <div style={S.row}>
            <span style={S.rowLabel}>Referral Source</span>
            <span style={S.rowValue}>{REFERRAL_SOURCES.find(s => s.value === ind.referralSource)?.label || ind.referralSource}</span>
          </div>
        )}
        {ind.assignedAgentName && (
          <div style={S.row}>
            <span style={S.rowLabel}>Case Manager</span>
            <span style={S.rowValue}>{ind.assignedAgentName}</span>
          </div>
        )}
        {ind.assignedClinicianName && (
          <div style={S.row}>
            <span style={S.rowLabel}>Clinician</span>
            <span style={S.rowValue}>{ind.assignedClinicianName}</span>
          </div>
        )}
        {ind.assignedPrescriberName && (
          <div style={S.row}>
            <span style={S.rowLabel}>Prescriber</span>
            <span style={S.rowValue}>{ind.assignedPrescriberName}</span>
          </div>
        )}
      </div>

      {/* Clinical History (if intake data exists) */}
      {(ind.presentingProblems || ind.historyMentalHealth || ind.historySubstanceUse) && (
        <div style={S.card}>
          <div style={S.sectionLabel}>
            <Heart size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            Clinical History
          </div>
          {ind.presentingProblems && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", marginBottom: 2 }}>Presenting Problems</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{ind.presentingProblems}</div>
            </div>
          )}
          {ind.historyMentalHealth && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", marginBottom: 2 }}>Mental Health History</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{ind.historyMentalHealth}</div>
            </div>
          )}
          {ind.historySubstanceUse && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", marginBottom: 2 }}>Substance Use History</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{ind.historySubstanceUse}</div>
            </div>
          )}
          {ind.historyMedical && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", marginBottom: 2 }}>Medical History</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{ind.historyMedical}</div>
            </div>
          )}
          {ind.historyTrauma && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", marginBottom: 2 }}>Trauma History</div>
              <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{ind.historyTrauma}</div>
            </div>
          )}
        </div>
      )}

      {/* Suicidal History Flag */}
      {ind.suicidalHistory && ind.suicidalHistory !== "none" && (
        <div style={{
          ...S.card, borderColor: "rgba(252,129,129,0.2)", background: "rgba(252,129,129,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={14} style={{ color: "#fc8181" }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: "#fc8181" }}>
              Suicidal History: {ind.suicidalHistory.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      )}

      {/* Emergency Contacts */}
      {(ind.emergencyContacts || []).length > 0 && (
        <div style={S.card}>
          <div style={S.sectionLabel}>Emergency Contacts</div>
          {ind.emergencyContacts.map((ec, i) => (
            <div key={i} style={{ marginBottom: i < ind.emergencyContacts.length - 1 ? 10 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{ec.name}</div>
              <div style={{ fontSize: 11, color: "#718096" }}>
                {ec.relation}{ec.phone && ` · ${ec.phone}`}
                {ec.isPrimary && <span style={{ color: "#f6ad55", marginLeft: 6, fontWeight: 700 }}>Primary</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Legacy emergency contact fields */}
      {!(ind.emergencyContacts || []).length && ind.emergencyContact && (
        <div style={S.card}>
          <div style={S.sectionLabel}>Emergency Contact</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{ind.emergencyContact}</div>
          <div style={{ fontSize: 11, color: "#718096" }}>
            {ind.emergencyRelation}{ind.emergencyPhone && ` · ${ind.emergencyPhone}`}
          </div>
        </div>
      )}
    </div>
  );
}